import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { DateOrIsoDateTimeSchema } from "../../core/iso-date-time.schema";

/**
 * Public-API alphabet for filtering by *last message role*. Distinct from
 * the raw `MessageContentType` Prisma enum — AGENT collapses
 * AI / FUNCTION / TOOL into one user-facing bucket so workflow authors
 * filtering for "agent-side last message" don't have to know about
 * tool-call rows. The persistence-side expansion is the inverse of this
 * mapping and lives in the backend chats infrastructure layer.
 */
export const ChatListLastMessageRoleSchema = z.enum(["USER", "AGENT", "SYSTEM"]);
export type ChatListLastMessageRole = z.infer<typeof ChatListLastMessageRoleSchema>;

/**
 * `ChatTicketStatus` mirrored as a Zod enum. Kept tuple-aligned with the
 * Prisma source via `DbEnum.CHAT_TICKET_STATUS_VALUES` so any schema-side
 * value rename forces a tsc break here.
 */
export const ChatTicketStatusEnumSchema = z.enum(DbEnum.CHAT_TICKET_STATUS_VALUES);

/**
 * Conflict-resolution refine for chat-list filter combinations. Applied via
 * `.superRefine(...)` so both V1 HTTP schemas (with `csvOrArray` transforms)
 * and the internal typed schema can share identical error-emission logic.
 *
 * Conflicts caught:
 *  - `ticketStatus` AND `ticketStatusIn` (use one or the other, not both)
 *  - `ticketStatusNot` equal to `ticketStatus` (no row could ever match)
 *  - `ticketStatusNot` inside `ticketStatusIn` (narrow the IN set instead)
 *  - `lastMessageBefore` ≤ `lastMessageAfter` (empty window)
 */
export interface ChatListFilterRefineShape {
  ticketStatus?: z.infer<typeof ChatTicketStatusEnumSchema>;
  ticketStatusIn?: ReadonlyArray<z.infer<typeof ChatTicketStatusEnumSchema>>;
  ticketStatusNot?: z.infer<typeof ChatTicketStatusEnumSchema>;
  lastMessageBefore?: Date;
  lastMessageAfter?: Date;
}

export function applyChatListFilterRefinements(
  v: ChatListFilterRefineShape,
  ctx: z.RefinementCtx
): void {
  if (v.ticketStatus && v.ticketStatusIn) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Send only ticketStatus OR ticketStatusIn, not both",
      path: ["ticketStatusIn"]
    });
  }
  if (v.ticketStatus && v.ticketStatusNot && v.ticketStatus === v.ticketStatusNot) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "ticketStatusNot cannot equal ticketStatus — no row could ever match",
      path: ["ticketStatusNot"]
    });
  }
  if (v.ticketStatusIn && v.ticketStatusNot && v.ticketStatusIn.includes(v.ticketStatusNot)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "ticketStatusNot must not be a member of ticketStatusIn — narrow ticketStatusIn instead",
      path: ["ticketStatusNot"]
    });
  }
  if (
    v.lastMessageBefore &&
    v.lastMessageAfter &&
    v.lastMessageBefore.getTime() <= v.lastMessageAfter.getTime()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "lastMessageBefore must be strictly after lastMessageAfter — otherwise the window is empty",
      path: ["lastMessageBefore"]
    });
  }
}

/**
 * Canonical typed schema for the chat-list filter set shared across the
 * public-API V1 surface (`GET /public/v1/conversations`), the workflow's
 * `inbox.list` action, and the upcoming `conversation.idle` trigger.
 *
 * Internal callers (workflow node, idle trigger) pass already-typed values
 * and should `.parse(...)` through this. The V1 HTTP schema layers its own
 * `csvOrArray` transforms on top to coerce query-string shapes, then funnels
 * into the same `applyChatListFilterRefinements` for identical conflict
 * semantics.
 *
 * The timestamps take `DateOrIsoDateTimeSchema` rather than `z.coerce.date()`
 * precisely because this schema has both kinds of caller: a `Date` from the
 * workflow node, an ISO-8601 string from anything persisting a listener config
 * as JSON. `z.coerce.date()` covered both by covering everything — including
 * `0` and `false`, which it turns into 1970-01-01 rather than rejecting.
 */
export const ChatListSharedFiltersSchema = z
  .object({
    ticketStatus: ChatTicketStatusEnumSchema.optional(),
    ticketStatusIn: z.array(ChatTicketStatusEnumSchema).min(1).optional(),
    ticketStatusNot: ChatTicketStatusEnumSchema.optional(),
    lastMessageBefore: DateOrIsoDateTimeSchema.optional(),
    lastMessageAfter: DateOrIsoDateTimeSchema.optional(),
    lastMessageTypeIn: z.array(ChatListLastMessageRoleSchema).min(1).optional(),
    commentContains: z.string().min(1).max(500).optional(),
    commentNotContains: z.string().min(1).max(500).optional()
  })
  .superRefine(applyChatListFilterRefinements);

export type ChatListSharedFilters = z.infer<typeof ChatListSharedFiltersSchema>;
